<template>
  <div class="fill-height">
    <v-card flat>
      <toolbar-period-selector
        v-model="dataTable.filter.filter_date"
        :default-period="null"
        :extension="showCompanyPackage"
        :mandatory="false"
        hide-year
      >
        <v-btn
          v-if="userCanManage && (userIsOwner || userIsSuperAdmin)"
          :color="$t('Receipt.Table.Toolbar.Button.Export.Color')"
          :disabled="!dataTable.filter.filter_date"
          :icon="$vuetify.breakpoint.xsOnly"
          :small="$vuetify.breakpoint.xsOnly"
          class="elevation-0 ma-1"
          text
          @click="onExportButtonClick"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">
            {{ $t('Receipt.Table.Toolbar.Button.Export.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Receipt.Table.Toolbar.Button.Export.Title').toLocaleUpperCase($i18n.locale) }}</span>
        </v-btn>

        <v-btn
          v-if="posUserHasComponentPermission('EftPosTransactions', 'ACCESS')"
          :color="$t('Receipt.Table.Toolbar.Button.Transactions.Color')"
          :icon="$vuetify.breakpoint.xsOnly"
          :small="$vuetify.breakpoint.xsOnly"
          :to="{name: 'EftPosTransactions'}"
          class="elevation-0 ma-1"
          text
        >
          <v-icon
            v-if="$t('Receipt.Table.Toolbar.Button.Transactions.Icon')"
            :left="$vuetify.breakpoint.smAndUp"
          >
            {{ $t('Receipt.Table.Toolbar.Button.Transactions.Icon') }}
          </v-icon>&nbsp;
          <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Receipt.Table.Toolbar.Button.Transactions.Title').toLocaleUpperCase($i18n.locale) }}</span>
        </v-btn>

        <v-btn
          v-if="userIsSuperAdmin && appHasFiscalDocsEnabled && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD"
          :disabled="runClearanceJobLoading"
          :loading="runClearanceJobLoading"
          class="elevation-0 ma-1 px-2"
          color="orange"
          dark
          depressed
          small
          @click="runClearanceReceiptsJob"
        >
          <span>RUN CLEARANCE JOB</span>
        </v-btn>

        <v-spacer />

        <filter-search v-model="dataTable.search.terms" />

        <filter-options
          v-model="filtersModel"
          :filters="filterData"
          max-width="450"
        />

        <template
          v-if="showCompanyPackage"
          #extension
        >
          <v-alert
            :color="companyPackageAlertColor"
            :style="companyPackage?.ProviderPortalUrl ? 'cursor: pointer;' : ''"
            border="left"
            class="ma-0 py-1 fill-width"
            dark
            dense
            prominent
            @click="onCompanyPackageClick"
          >
            <v-container
              class="pa-0"
              fluid
              style="opacity: 0.90;"
            >
              <v-row
                align="center"
                dense
              >
                <v-col class="shrink pl-3 pr-4">
                  <v-progress-circular
                    :value="companyPackage?.RemainingPercentage || 0"
                    color="white"
                    size="46"
                    width="6"
                  >
                    <span style="font-size: 12px;">{{ parseInt(companyPackage?.RemainingPercentage || 0) }}%</span>
                  </v-progress-circular>
                </v-col>

                <v-col class="grow">
                  <div class="text-subtitle-1 font-weight-bold">
                    {{ companyPackage?.PackageName || 'Provider' }} - <span class="text-capitalize font-weight-regular">{{ companyPackage?.Provider || appFiscalDocsProvider }}</span>
                  </div>
                  <div class="text-caption">
                    <strong>{{ $t('Receipt.Table.Toolbar.Info.CompanyPackage.InitialValue') }}</strong> {{ companyPackage?.InitialValue | currency }}
                    (<strong>{{ $t('Receipt.Table.Toolbar.Info.CompanyPackage.RemainingValue') }}</strong> {{ companyPackage?.RemainingValue | currency }})
                  </div>
                </v-col>

                <v-col class="auto text-right text-caption">
                  <div v-if="companyPackage?.ValidFrom">
                    <strong>{{ $t('Receipt.Table.Toolbar.Info.CompanyPackage.ValidFrom') }}</strong> {{ companyPackage?.ValidFrom }}
                  </div>
                  <div v-if="companyPackage?.ValidTo">
                    <strong>{{ $t('Receipt.Table.Toolbar.Info.CompanyPackage.ValidTo') }}</strong> {{ companyPackage?.ValidTo }}
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-alert>
        </template>
      </toolbar-period-selector>

      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col cols="12">
            <v-data-table
              :footer-props="dataTable.footerProps"
              :headers="dataTableHeaders"
              :height="dataTable.resize.bodyHeight"
              :items="dataTable.data"
              :loading="dataTable.loading"
              :loading-text="dataTable.text.loadingText"
              :no-data-text="dataTable.text.noDataText"
              :no-results-text="dataTable.text.noResultsText"
              :options.sync="dataTable.options"
              :search="dataTable.search.terms"
              :server-items-length="dataTable.total"
              fixed-header
              item-key="Id"
            >
              <template #[`item.id`]="{item}">
                <span>{{ item.Id }}</span>
              </template>

              <template #[`item.type`]="{item}">
                <span :class="receiptTypeColor(item, true)">{{ item.InvoiceTypeCode }} {{ item.InvoiceTypeName }}</span>

                <div
                  v-if="(item?.CompanyId && item?.Company) || item?.VatNumber"
                  class="my-1"
                  style="font-size: 10px; line-height: 14px"
                >
                  <div v-if="item?.CompanyId && item?.Company">
                    <strong>{{ $tc('Customer.Title', 1).toLocaleUpperCase($i18n.locale) }}:</strong> {{ item.Company.BrandName }}
                  </div>
                  <div v-if="item?.VatNumber">
                    <strong>{{ $t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Label').toLocaleUpperCase($i18n.locale) }}:</strong> {{ item.VatNumber }}
                  </div>
                </div>
              </template>

              <template #[`item.table`]="{item}">
                {{ item.Table }}

                <v-btn
                  v-if="item.Table"
                  icon
                  x-small
                  @click="searchByTableName(item)"
                >
                  <v-icon>mdi-select-search</v-icon>
                </v-btn>
              </template>

              <template #[`item.number`]="{item}">
                <div class="text-no-wrap">
                  <v-chip
                    class="align-center justify-center font-weight-bold"
                    dark
                    label
                    style="min-width: 100px;"
                    x-small
                    @click="showReceiptDetailsDialog(item.Id)"
                  >
                    {{ item.InternalId }}
                  </v-chip>

                  <v-btn
                    v-if="item.DocumentUrl"
                    :href="item.DocumentUrl"
                    icon
                    target="_blank"
                    x-small
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>
              </template>

              <template #[`item.total`]="{item}">
                <v-tooltip
                  :color="isDarkMode ? '#191919' : 'grey lighten-5'"
                  :disabled="!item.PaymentMethods || (item.PaymentMethods && !item.PaymentMethods.length)"
                  bottom
                >
                  <template #activator="{on}">
                    <div v-on="on">
                      <v-badge color="transparent">
                        <span>{{ item.AmountFmt }}</span>

                        <template
                          v-if="item.PaymentMethods && item.PaymentMethods.length"
                          #badge
                        >
                          <v-icon
                            color="info"
                            x-small
                          >
                            mdi-information-variant-circle-outline
                          </v-icon>
                        </template>
                      </v-badge>
                    </div>
                  </template>

                  <v-card
                    color="transparent"
                    flat
                  >
                    <v-card-title class="justify-center font-weight-bold text-body-1">
                      {{ $tc('Restaurant.Tables.Dialog.Payment.Title', 0) }}
                    </v-card-title>

                    <v-list
                      color="transparent"
                      dense
                      flat
                    >
                      <v-list-item
                        v-for="(payment, paymentIdx) in item.PaymentMethods"
                        :key="paymentIdx"
                        class="text-left"
                      >
                        <v-list-item-avatar :color="getPaymentTypeLangByCode(payment.Code).Color">
                          <v-icon
                            dark
                            v-text="getPaymentTypeLangByCode(payment.Code).Icon"
                          />
                        </v-list-item-avatar>

                        <v-list-item-content>
                          <v-list-item-title>
                            {{ getPaymentTypeLangByCode(payment.Code).Title }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ payment.Amount | currency }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-tooltip>
              </template>

              <template #[`item.aade`]="{item}">
                <template v-if="item.Mark && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                  <v-tooltip
                    :color="isDarkMode ? '#191919' : 'grey lighten-5'"
                    bottom
                    class="pa-0"
                  >
                    <template #activator="{on}">
                      <div v-on="on">
                        <v-badge
                          color="transparent"
                          offset-x="6"
                          offset-y="16"
                          overlap
                        >
                          <div @click="copyToClipboard(item.Mark)">
                            <my-data-logo
                              class="ma-1"
                              img-class="pa-1"
                              style="cursor: pointer"
                              width="55"
                            />
                          </div>

                          <template #badge>
                            <v-icon
                              color="info"
                              x-small
                            >
                              mdi-information-variant-circle-outline
                            </v-icon>
                          </template>
                        </v-badge>
                      </div>
                    </template>

                    <div
                      :class="isDarkMode ? 'white--text' : 'black--text'"
                      class="text-caption pa-2 text-center"
                    >
                      <my-data-logo
                        class="mb-2"
                        img-class="py-1 px-3"
                        width="125"
                      />

                      <div class="font-weight-medium">
                        {{ $t('Order.OrderDetailsDialog.Receipt.Submitted') }}
                      </div>

                      <div class="my-4" />

                      <div class="text-left">
                        <div>{{ $t('Order.OrderDetailsDialog.Receipt.Mark') }}</div>
                        <div class="grey--text">
                          {{ item.Mark }}
                        </div>

                        <div class="my-2" />

                        <div>{{ $t('Order.OrderDetailsDialog.Receipt.Uid') }}</div>
                        <div class="grey--text">
                          {{ item.Uid }}
                        </div>

                        <div class="my-2" />

                        <div>{{ $t('Order.OrderDetailsDialog.Receipt.AuthCode') }}</div>
                        <div class="grey--text">
                          {{ item.AuthenticationCode }}
                        </div>

                        <div class="my-2" />

                        <div>{{ $t('Order.OrderDetailsDialog.Receipt.Provider') }}</div>
                        <div class="grey--text">
                          {{ item.Provider }}
                        </div>
                      </div>
                    </div>
                  </v-tooltip>
                </template>
              </template>

              <template #[`item.status`]="{item}">
                <div>
                  <v-tooltip
                    :color="receiptStatus(item).Color"
                    bottom
                    max-width="450"
                  >
                    <template #activator="{on}">
                      <v-chip
                        :color="receiptStatus(item).Color"
                        class="align-center justify-center font-weight-bold mr-5"
                        dark
                        label
                        style="min-width: 100px;"
                        x-small
                        v-on="on"
                      >
                        {{ receiptStatus(item).Name.toLocaleUpperCase($i18n.locale) }}
                      </v-chip>
                    </template>

                    <template v-if="(receiptHasError(item) || item?.CanRetry) && receiptErrors(item).length">
                      <div class="text-center">
                        <h4 class="mb-2">
                          {{ item.InvoiceTypeCode }} {{ item.InvoiceTypeName }}
                        </h4>

                        <template v-for="(error, index) in receiptErrors(item)">
                          <div
                            :key="index"
                            class="text-left mb-1"
                          >
                            <span class="font-weight-bold">{{ error.code }}</span> - {{ error.message }}
                          </div>
                        </template>
                      </div>
                    </template>

                    <template v-else>
                      <div class="text-center">
                        <h4 class="mb-2">
                          {{ item.InvoiceTypeCode }} {{ item.InvoiceTypeName }}
                        </h4>

                        <div class="font-weight-bold">
                          {{ receiptStatus(item).Name.toLocaleUpperCase($i18n.locale) }}
                        </div>
                      </div>
                    </template>
                  </v-tooltip>
                </div>

                <div
                  v-if="item.InvoiceTypeCode === FiscalDocTypeCodeEnum.GREEK_DOCUMENT_ORDER && item.Related && item.Related.Receipt && item.Related.Receipt.InternalId"
                  class="text-no-wrap"
                >
                  <v-chip
                    class="align-center justify-center font-weight-bold"
                    color="grey"
                    dark
                    label
                    style="min-width: 100px;"
                    x-small
                    @click="showReceiptDetailsDialog(item.Related.Receipt.Id)"
                  >
                    {{ item.Related.Receipt.InternalId }}
                  </v-chip>

                  <v-btn
                    v-if="item.Related.Receipt.DocumentUrl"
                    :href="item.Related.Receipt.DocumentUrl"
                    color="grey"
                    icon
                    target="_blank"
                    x-small
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>
              </template>

              <template #[`item.related`]="{item}">
                <div
                  v-if="item.Related && item.Related.Canceled && item.Related.Canceled.InternalId"
                  class="text-no-wrap my-1"
                >
                  <v-chip
                    :color="receiptTypeColor(item.Related.Canceled)"
                    class="align-center justify-center font-weight-bold"
                    dark
                    label
                    style="min-width: 100px;"
                    x-small
                    @click="showReceiptDetailsDialog(item.Related.Canceled.Id)"
                  >
                    {{ item.Related.Canceled.InternalId }}
                  </v-chip>

                  <v-btn
                    v-if="item.Related.Canceled.DocumentUrl"
                    :color="receiptTypeColor(item.Related.Canceled)"
                    :href="item.Related.Canceled.DocumentUrl"
                    icon
                    target="_blank"
                    x-small
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>

                <div
                  v-if="item.Related && item.Related.CanceledByMark && item.Related.CanceledByMark.InternalId"
                  class="text-no-wrap my-1"
                >
                  <v-chip
                    :color="receiptTypeColor(item.Related.CanceledByMark)"
                    class="align-center justify-center font-weight-bold"
                    dark
                    label
                    style="min-width: 100px;"
                    x-small
                    @click="showReceiptDetailsDialog(item.Related.CanceledByMark.Id)"
                  >
                    {{ item.Related.CanceledByMark.InternalId }}
                  </v-chip>

                  <v-btn
                    v-if="item.Related.CanceledByMark.DocumentUrl"
                    :color="receiptTypeColor(item.Related.CanceledByMark)"
                    :href="item.Related.CanceledByMark.DocumentUrl"
                    icon
                    target="_blank"
                    x-small
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>

                <template v-if="item.Related && item.Related.Fnbs && Array.isArray(item.Related.Fnbs)">
                  <template v-for="(fnb, index) in item.Related.Fnbs">
                    <div
                      :key="`fnb-${fnb.Id}-${index}`"
                      class="text-no-wrap my-1"
                    >
                      <v-chip
                        :color="receiptTypeColor(fnb)"
                        class="align-center justify-center font-weight-bold"
                        dark
                        label
                        style="min-width: 100px;"
                        x-small
                        @click="showReceiptDetailsDialog(fnb.Id)"
                      >
                        {{ fnb.InternalId }}
                      </v-chip>

                      <v-btn
                        v-if="fnb.DocumentUrl"
                        :color="receiptTypeColor(fnb)"
                        :href="fnb.DocumentUrl"
                        icon
                        target="_blank"
                        x-small
                      >
                        <v-icon>mdi-open-in-new</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </template>

                <template v-if="item.Related && item.Related.Receipts && Array.isArray(item.Related.Receipts)">
                  <template v-for="(receipt, index) in item.Related.Receipts">
                    <div
                      :key="`receipt-${receipt.Id}-${index}`"
                      class="text-no-wrap my-1"
                    >
                      <v-chip
                        :color="receiptTypeColor(receipt)"
                        class="align-center justify-center font-weight-bold"
                        dark
                        label
                        style="min-width: 100px;"
                        x-small
                        @click="showReceiptDetailsDialog(receipt.Id)"
                      >
                        {{ receipt.InternalId }}
                      </v-chip>

                      <v-btn
                        v-if="receipt.DocumentUrl"
                        :color="receiptTypeColor(receipt)"
                        :href="receipt.DocumentUrl"
                        icon
                        target="_blank"
                        x-small
                      >
                        <v-icon>mdi-open-in-new</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </template>
              </template>

              <template #[`item.user`]="{item}">
                <user-chip
                  v-if="item.UserId > 0"
                  :color="item.UserColor"
                  :full-name="item.User"
                  :short-name="item.UserShortName"
                />
              </template>

              <template #[`item.notify`]="{item}">
                <receipt-notify-details :receipt="item" />
              </template>

              <template #[`item.date`]="{item}">
                <div class="text-no-wrap">
                  {{ item.CreatedAt }}
                </div>
                <div
                  v-if="item.CanceledAt"
                  class="text-no-wrap error--text"
                >
                  {{ item.CanceledAt }}
                </div>
              </template>


              <template #[`item.actions`]="{item}">
                <template
                  v-if="appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD || (appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.LOCAL && appFiscalDocsProvider === FiscalDocsProviderEnum.GENERIC && item.HasOrder)"
                >
                  <v-speed-dial
                    v-if="!receiptHasError(item)"
                    v-model="item.fab"
                    direction="left"
                    transition="slide-x-reverse-transition"
                  >
                    <template #activator>
                      <v-btn
                        :small="$vuetify.breakpoint.xsOnly"
                        class="blue-grey lighten-2 elevation-2 ma-0"
                        dark
                        icon
                      >
                        <v-icon
                          v-if="item.fab"
                          :small="$vuetify.breakpoint.xsOnly"
                        >
                          close
                        </v-icon>
                        <v-icon
                          v-else
                          :small="$vuetify.breakpoint.xsOnly"
                        >
                          menu
                        </v-icon>
                      </v-btn>
                    </template>

                    <print-receipt-button
                      v-if="userCanPrintFiscalDoc && appMachineHasFiscalDocPrinters && !receiptHasError(item) && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderFiscal"
                      :small="$vuetify.breakpoint.xsOnly"
                      :small-icon="$vuetify.breakpoint.xsOnly"
                      :small-size="28"
                      class="ml-1"
                      hide-label
                      icon
                      @fiscal-document:print="$bus.$emit('fiscal-document:print', item.Id)"
                    />

                    <email-receipt-button
                      v-if="userCanViewFiscalDoc && appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL) && !receiptHasError(item)"
                      :small="$vuetify.breakpoint.xsOnly"
                      :small-icon="$vuetify.breakpoint.xsOnly"
                      :small-size="28"
                      class="ml-1"
                      hide-label
                      icon
                      @fiscal-document:email="$bus.$emit('fiscal-document:email', item.Id, null, {email: true, From: EmailNotifyFromEnum.RECEIPT})"
                    />

                    <template v-if="userInAdminGroup && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                      <v-tooltip
                        :color="$t('Customer.Table.Button.ViewCustomer.Color')"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :class="$t('Customer.Table.Button.ViewCustomer.Color')"
                            :small="$vuetify.breakpoint.xsOnly"
                            class="ma-0 ml-1"
                            dark
                            icon
                            @click="showReceiptDetailsDialog(item.Id)"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              {{ $t('Customer.Table.Button.ViewCustomer.Icon') }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('Common.Button.ViewDocument') }}</span>
                      </v-tooltip>
                    </template>

                    <template v-if="item.DocumentUrl">
                      <v-tooltip
                        color="light-green"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :href="item.DocumentUrl"
                            :small="$vuetify.breakpoint.xsOnly"
                            class="light-green ma-0 ml-1"
                            dark
                            icon
                            target="_blank"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              mdi-open-in-new
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('Common.Button.ViewDocument') }}</span>
                      </v-tooltip>
                    </template>

                    <template v-if="canRetryReceipt(item)">
                      <v-tooltip
                        color="orange"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :small="$vuetify.breakpoint.xsOnly"
                            class="orange ma-0 ml-1"
                            dark
                            icon
                            @click="retryReceipt(item)"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              mdi-cloud-upload-outline
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('Common.Button.RetryDocument') }}</span>
                      </v-tooltip>
                    </template>

                    <template v-if="canCancelReceipt(item)">
                      <v-tooltip
                        :color="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :class="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                            :small="$vuetify.breakpoint.xsOnly"
                            class="ma-0 ml-1"
                            dark
                            icon
                            @click="cancelReceipt(item)"
                            v-on="on"
                          >
                            <v-icon :small="$vuetify.breakpoint.xsOnly">
                              {{ $t('Order.OrdersAll.ListView.Button.Cancel.Icon') }}
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('Order.OrderDetailsDialog.Receipt.Cancel') }}</span>
                      </v-tooltip>
                    </template>
                  </v-speed-dial>
                </template>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <receipt-details-dialog
      v-if="selectedDocument"
      :receipt="selectedDocument"
      :visible.sync="receiptDetailsDialogVisible"
    />

    <email-select-dialog
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </div>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import DataTable from '@/mixins/data-table/dataTable'
import FilterCommon from '@/mixins/filter/filterCommon'
import FilterSearch from '@/components/common/filter/FilterSearch'
import FilterOptions from '@/components/common/filter/FilterOptions'
import PaymentType from '@/mixins/orders/paymentType'
import EmailReceiptButton from '@/components/common/receipt/EmailReceiptButton'
import PrintReceiptButton from '@/components/common/receipt/PrintReceiptButton'
import ReceiptDetailsDialog from '@/components/receipts/ReceiptDetailsDialog'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import MyDataLogo from '@/components/receipts/MyDataLogo.vue'
import ReceiptsCommon from '@/mixins/receipts/receiptsCommon'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import FiscalDocTypeCodeEnum from '@/api/Enums/FiscalDocTypeCodeEnum'
import ToolbarPeriodSelector from '@/components/common/ToolbarPeriodSelector.vue'
import PinManagerEnum from '@/api/Enums/PinManagerEnum.js'
import {getContrastColor} from '@/lib/color/index.js'
import ReceiptNotifyDetails from '@/components/receipts/ReceiptNotifyDetails.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import FiscalDocsProviderEnum from '@/api/Enums/FiscalDocsProviderEnum.js'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'Receipts',
  components: {
    UserChip,
    EmailSelectDialog,
    ReceiptNotifyDetails,
    ToolbarPeriodSelector,
    MyDataLogo,
    ReceiptDetailsDialog,
    PrintReceiptButton,
    EmailReceiptButton,
    FilterSearch,
    FilterOptions
  },
  directives: {},
  mixins    : [AppData, Auth, DataTable, FilterCommon, PaymentType, ReceiptsCommon],
  props     : {
    searchParam: {
      type   : String,
      default: ''
    }
  },
  data() {
    return {
      cancel                     : true,
      selectedDocument           : null,
      emailSelectDialogVisible   : false,
      receiptDetailsDialogVisible: false,
      runClearanceJobLoading     : false,
      companyPackage             : null,
      filtersModel               : {
        lang         : 'Receipt.Table',
        filterFailure: {
          key  : 'filter_failure',
          value: null,
          items: this.$t('Receipt.Table.Filter.filterFailure.Items')
        },
        filterWithoutOpenHours: {
          key  : 'filter_without_open_hours',
          value: 'false',
          items: this.$t('Receipt.Table.Filter.filterWithoutOpenHours.Items')
        },
        filterStatus: {
          key  : 'filter_status',
          value: [],
          items: []
        },
        filterInvoiceType: {
          key  : 'filter_invoice_type',
          value: [],
          items: []
        },
        filterCompany: {
          key  : 'filter_company',
          value: [],
          items: []
        },
        filterUser: {
          key  : 'filter_user',
          value: [],
          items: []
        }
      },
      dataTable: {
        dataStore    : null,
        socketCommand: {
          list: window.SocketCommand.Receipt.All
        },
        filter: {
          filter_date: null
        },
        options: {
          defaultSortBy: 'order_receipt_id',
          sortBy       : ['order_receipt_id'],
          sortDesc     : [true]
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Receipt.Table.Headers.Id'),
            value    : 'id',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Type'),
            value    : 'type',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Table'),
            value    : 'table',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Number'),
            value    : 'number',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Total'),
            value    : 'total',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          ...(window.appConfig?.LOCATION_DATA?.ReceiptsProviderType === FiscalDocsProviderTypeEnum.CLOUD ? [{
            text     : this.$t('Receipt.Table.Headers.Aade'),
            value    : 'aade',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          }] : []),
          {
            text     : this.$t('Receipt.Table.Headers.Status'),
            value    : 'status',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Related'),
            value    : 'related',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.User'),
            value    : 'user',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Notify'),
            value    : 'notify',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Date'),
            value    : 'date',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Receipt.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption px-2',
            width    : '50px'
          }
        ]
      }
    }
  },
  computed: {
    FiscalDocsProviderEnum() {
      return FiscalDocsProviderEnum
    },
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    FiscalDocTypeCodeEnum() {
      return FiscalDocTypeCodeEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },
    NotifyTypeEnum() {
      return NotifyTypeEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    canGetCompanyPackage() {
      return this.userCanViewFiscalDoc && this.appFiscalDocsProvider === FiscalDocsProviderEnum.IMPACT
    },
    showCompanyPackage() {
      return this.canGetCompanyPackage && this.$vuetify.breakpoint.smAndUp
    },
    companyPackageAlertColor() {
      return this.companyPackage?.RemainingPercentage < 5 ? 'red' : this.companyPackage?.RemainingPercentage < 10 ? 'warning' : 'info'
    }
  },
  watch: {},
  created() {
    // console.log('1. created');
    this.$bus.$on(window.SocketCommand.Receipt.Get, this.onReceiptDetailsResponse)
    this.$bus.$on(window.SocketCommand.Receipt.Filters, this.onFiltersDataResponse)
    this.$bus.$on(window.SocketCommand.Receipt.CompanyPackage, this.onCompanyPackageDataResponse)
    this.$bus.$on(window.SocketCommand.Receipt.Export, this.onExportResult)
    this.$bus.$on(window.SocketCommand.Ajax.Get, this.updateData)
    this.$bus.$on('receipts:filterOpenFiscalDocuments', this.filterOpenFiscalDocuments)

    this.getFiltersData()
    this.getCompanyPackage()

    // Init Custom Filter Values
    this.filtersModel.filterStatus.items = Object.values(this.$t('Receipt.Status'))
    this.filtersModel.filterInvoiceType.items = []
  },
  mounted() {
    // console.log('2. mounted');
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Receipt.Get, this.onReceiptDetailsResponse)
    this.$bus.$off(window.SocketCommand.Receipt.Filters, this.onFiltersDataResponse)
    this.$bus.$off(window.SocketCommand.Receipt.CompanyPackage, this.onCompanyPackageDataResponse)
    this.$bus.$off(window.SocketCommand.Receipt.Export, this.onExportResult)
    this.$bus.$off(window.SocketCommand.Ajax.Get, this.updateData)
    this.$bus.$off('receipts:filterOpenFiscalDocuments', this.filterOpenFiscalDocuments)
  },
  methods: {
    getContrastColor,

    searchByTableName(item) {
      this.dataTable.search.terms = item.Table
    },

    cancelReceipt(item) {
      this.executeWithPinPermissionManager(PinManagerEnum.DocumentCancel, (success) => {
        if (!success) return

        this.$bus.$emit('fiscal-document:cancel', {
          ...item,
          ReceiptId    : item.Id,
          TableId      : null,
          BroadcastList: true
        }, {refund: false})
      })
    },

    retryReceipt(item) {
      this.$bus.$emit('fiscal-document:retry', item.Id)
    },

    showReceiptDetailsDialog(itemId) {
      if (!this.userInAdminGroup || this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD) return
      window.callAS(window.SocketCommand.Receipt.Get, {Id: itemId})
    },
    onReceiptDetailsResponse(item) {
      this.selectedDocument = item
      this.receiptDetailsDialogVisible = true
    },

    onCompanyPackageClick() {
      if (!this.companyPackage?.ProviderPortalUrl) return
      const win = window.open(this.companyPackage.ProviderPortalUrl, '_blank')
      if (win) win.focus()
    },

    showOrderDetailsDialog(item) {
      window.callAS(window.SocketCommand.Order.GetDetails, {
        order_id: item.OrderId
      })
    },

    getCompanyPackage() {
      if (!this.canGetCompanyPackage) return
      window.callAS(window.SocketCommand.Receipt.CompanyPackage, {})
    },
    onCompanyPackageDataResponse(data) {
      if (data.status === 'fail') return
      this.companyPackage = data || null
    },

    getFiltersData() {
      window.callAS(window.SocketCommand.Receipt.Filters, {})
    },
    onFiltersDataResponse(data) {
      const companies = Array.isArray(this.$DataStore.companies) ? this.$DataStore.companies : []
      this.filtersModel.filterInvoiceType.items = data?.InvoiceTypes || {}
      this.filtersModel.filterUser.items = data?.Users || {}
      this.filtersModel.filterCompany.items = companies.map(company => {
        return {
          Id  : company.Id,
          Name: company.VatNumber + ' - ' + company.BrandName
        }
      })
      if (this.$route.params.hasOpenFiscalDocuments) this.filterOpenFiscalDocuments()
    },

    filterOpenFiscalDocuments() {
      this.$nextTick(() => {
        if (!this.filtersModel.filterStatus.value.includes(5)) this.filtersModel.filterStatus.value.push(5)
        if (!this.filtersModel.filterInvoiceType.value.includes(FiscalDocTypeCodeEnum.GREEK_DOCUMENT_ORDER)) this.filtersModel.filterInvoiceType.value.push(FiscalDocTypeCodeEnum.GREEK_DOCUMENT_ORDER)
      })
    },

    updateData(payload) {
      const command = window.SocketCommand.Receipt.All
      const payloadCommandsArray = payload?.command ? Array.isArray(payload?.command) ? payload.command : [payload.command] : []

      if (payloadCommandsArray.length) {
        payloadCommandsArray.forEach(cmd => {
          const payloadCommand = window.SocketCommand.commandToClientFormat(cmd || '')
          if (command === payloadCommand) this.getData()
        })
      }
    },

    runClearanceReceiptsJob() {
      window.callAS(window.SocketCommand.Order.RunClearanceJob, {})
      this.runClearanceJobLoading = true
    },

    onExportButtonClick() {
      if (!this.dataTable?.filter?.filter_date) return
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails) {
      if (!Array.isArray(emails) || !emails?.length) return

      const dataTablePayload = this.dataTableRequestData()
      const payload = {
        Emails          : emails,
        Search          : this.dataTable.search,
        Period          : this.dataTable.filter.filter_date || null,
        Failure         : this.filtersModel?.filterFailure?.value || null,
        Status          : this.filtersModel?.filterStatus?.value || null,
        InvoiceType     : this.filtersModel?.filterInvoiceType?.value || null,
        UserId          : this.filtersModel?.filterUser?.value || null,
        Company         : this.filtersModel?.filterCompany?.value || null,
        WithoutOpenHours: this.filtersModel?.filterWithoutOpenHours?.value || false,
        SortBy          : dataTablePayload.sort_by,
        OrderBy         : dataTablePayload.order_by
      }

      window.callAS(window.SocketCommand.Receipt.Export, payload)
    },

    onExportResult(response) {
      if (response.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Receipt.Table.Toolbar.Button.Export.Success', {email: this.posUser?.staff_email || ''}),
          type   : 'success',
          icon   : 'check',
          timeout: 5000
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    }
  }
}
</script>

<style scoped>
:deep(.v-toolbar__extension) {
  padding: 0 10px;
}

:deep(.v-progress-circular__underlay) {
  stroke: rgba(255, 255, 255, 0.16);
}

.v-tooltip__content.menuable__content__active {
  opacity: 1;
}
</style>
